import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ResourceSelect from "../../../../../../components/shared/inputs/ResourceSelect";
import HourTypeSelect from "../../../../../../components/shared/inputs/HourTypeSelect";
import Select from "../../../../../../components/shared/inputs/Select";
import { useGroups } from "../../../../General/OrganisationLevelGroups/context";
import { 
    getSettingsOrgResourceSearchFilters, 
    getSettingsResourceSearchFilters, 
    renderOrgLevelResourceLabel, 
    renderSettingsResourceLabel 
} from "../../../../../../utils/helpers/settings";

export const useFiltersConfig = () => {
    const { t } = useTranslation();
    const { groups } = useGroups();

    const renderHomeLabel = useCallback((option: any) => {
        const { code, description, glSegment } = option;
        return t("group-home", {
            group: glSegment || code + "-" + description
        });
    }, [t]);

    const renderLabel = useCallback((option: any) => {
        const { code, description, glSegment } = option;
        return t("group-worked", {
            group: glSegment || code + "-" + description
        });
    }, [t]);

    return useMemo(() => {
        const config = [
            {
                name: "employeeType",
                label: t("employee-type"),
                type: "resource-select",
                component: (
                    <ResourceSelect
                        labelPropName="description"
                        renderLabel={renderSettingsResourceLabel}
                        resourcePath="/employee-type"
                        mode="multiple"
                        getSearchFilter={getSettingsResourceSearchFilters}
                    />
                ),
            },
            {
                name: "locationHome",
                label: t("locations-home"),
                type: "resource-select",
                component: (
                    <ResourceSelect
                        resourcePath="/locations"
                        mode="multiple"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilter={getSettingsResourceSearchFilters}
                        params={{ pagination: "off" }}
                    />
                ),
            },
            {
                name: "groupingHome",
                label: t("groupings-home"),
                type: "resource-select",
                component: (
                    <ResourceSelect
                        resourcePath="/grouping"
                        mode="multiple"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilter={getSettingsResourceSearchFilters}
                        params={{ pagination: "off" }}
                    />
                ),
            },
            {
                name: "hourType",
                label: t("hour-type"),
                type: "select",
                component: <HourTypeSelect mode="multiple" />,
            },
            {
                name: "job",
                label: t("job"),
                type: "resource-select",
                component: (
                    <ResourceSelect
                        resourcePath="/jobs"
                        mode="multiple"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilter={getSettingsResourceSearchFilters}
                        params={{ pagination: "off" }}
                    />
                ),
            },
            {
                name: "location",
                label: t("locations-worked"),
                type: "resource-select",
                component: (
                    <ResourceSelect
                        resourcePath="/locations"
                        mode="multiple"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilter={getSettingsResourceSearchFilters}
                        params={{ pagination: "off" }}
                    />
                ),
            },
            {
                name: "payCode",
                label: t("pay-code"),
                type: "resource-select",
                component: (
                    <ResourceSelect
                        resourcePath="/pay-code"
                        mode="multiple"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilter={getSettingsResourceSearchFilters}
                        params={{
                            pagination: "off",
                            withoutLookups: true
                        }}
                    />
                ),
            },
            {
                name: "status",
                label: t("status"),
                type: "select",
                component: (
                    <Select>
                        <Select.Item value="publish">
                            {t("publish")}
                        </Select.Item>
                        <Select.Item value="unpublish">
                            {t("unpublish")}
                        </Select.Item>
                        <Select.Item value="lock">
                            {t("locked")}
                        </Select.Item>
                        <Select.Item value="unlock">
                            {t("unlocked")}
                        </Select.Item>
                    </Select>
                )
            }
        ];

        if (groups?.length > 0) {
            config.splice(3, 0, ...groups.map((group: any) => ({
                name: `${group.level.replace("_", "")}Home`,
                label: renderHomeLabel(group),
                type: "resource-select",
                component: (
                    <ResourceSelect
                        mode="multiple"
                        resourcePath={`/${group?.id}/org-levels`}
                        renderLabel={renderOrgLevelResourceLabel}
                        getSearchFilter={getSettingsOrgResourceSearchFilters}
                    />
                ),
            })))
        }

        if (groups?.length > 0) {
            config.splice(3 + groups.length, 0, ...groups.map((group: any) => ({
                name: `${group.level.replace("_", "")}`,
                label: renderLabel(group),
                type: "resource-select",
                component: (
                    <ResourceSelect
                        mode="multiple"
                        resourcePath={`/${group?.id}/org-levels`}
                        renderLabel={renderOrgLevelResourceLabel}
                        getSearchFilter={getSettingsOrgResourceSearchFilters}
                    />
                ),
            })))
        }

        return config;
    }, [groups, renderLabel, renderHomeLabel, t]);
}
